/**
 * MIRA Robot — the little flying companion.
 *
 * Pure SVG + framer-motion. Soft white orb, cyan eyes, faint gold rim,
 * particle wings, gentle breathing + hover loop. Speech bubble is optional
 * (pass `say="..."`).
 */
import React from 'react';
import { motion } from 'framer-motion';

const MiraRobot = ({ size = 120, say, mood = 'happy' }) => {
  // Slow up-and-down "breathing" loop
  const float = {
    animate: { y: [0, -8, 0] },
    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
  };

  // The eye expression — for MVP-1 we ship "happy" (warm arcs) + a sleep mood
  const eyes = mood === 'sleep' ? (
    <>
      <path d="M38 52 q6 -6 12 0" stroke="#06b6d4" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M70 52 q6 -6 12 0" stroke="#06b6d4" strokeWidth="3" fill="none" strokeLinecap="round" />
    </>
  ) : (
    <>
      <circle cx="44" cy="54" r="5" fill="#06b6d4" />
      <circle cx="76" cy="54" r="5" fill="#06b6d4" />
      <circle cx="42.5" cy="52.5" r="1.4" fill="#ffffff" />
      <circle cx="74.5" cy="52.5" r="1.4" fill="#ffffff" />
    </>
  );

  return (
    <div className="relative inline-flex flex-col items-center" data-testid="mira-robot">
      <motion.div {...float} style={{ width: size, height: size }} className="relative">
        {/* Outer cyan glow */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            boxShadow: '0 0 60px 8px rgba(34,211,238,0.25), 0 0 120px 16px rgba(34,211,238,0.12)',
          }}
        />
        {/* Particle wings */}
        <motion.div
          className="absolute -left-6 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-300/60"
          animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.7, 1.1, 0.7] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -right-6 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-300/60"
          animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.7, 1.1, 0.7] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', delay: 1.1 }}
        />
        {/* The orb itself */}
        <svg viewBox="0 0 120 120" className="relative w-full h-full">
          <defs>
            <radialGradient id="orbBody" cx="38%" cy="32%" r="70%">
              <stop offset="0%"  stopColor="#ffffff" />
              <stop offset="55%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#e2e8f0" />
            </radialGradient>
            <linearGradient id="orbRim" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#fde68a" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#facc15" stopOpacity="0.25" />
            </linearGradient>
          </defs>
          {/* gold rim (very faint) */}
          <circle cx="60" cy="60" r="48" fill="none" stroke="url(#orbRim)" strokeWidth="1.5" />
          {/* body */}
          <circle cx="60" cy="60" r="46" fill="url(#orbBody)" stroke="#e2e8f0" strokeWidth="1" />
          {/* glossy highlight */}
          <ellipse cx="46" cy="40" rx="14" ry="8" fill="#ffffff" opacity="0.7" />
          {/* mouth (very subtle smile arc) */}
          <path d="M48 76 q12 8 24 0" stroke="#cbd5e1" strokeWidth="2" fill="none" strokeLinecap="round" />
          {eyes}
        </svg>
      </motion.div>

      {say && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="mt-3 max-w-[260px] px-4 py-2 rounded-2xl bg-white shadow-[0_6px_24px_-8px_rgba(15,23,42,0.18)] ring-1 ring-slate-100 text-sm text-slate-700 text-center"
        >
          {say}
        </motion.div>
      )}
    </div>
  );
};

export default MiraRobot;
